import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateProject, useProject, useProjects, useUpdateProject } from "@/api/queries";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { ErrorState } from "@/components/ErrorState";
import { Field, TextInput } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { useUiStore } from "@/store/ui";

const NEW_PROJECT_VALUE = "__new__";

/**
 * 项目切换、新建与重命名（specs/14 §5.2 / §5.3）。
 *
 * - 项目列表来自 `GET /projects`，不回落任何本地项目；
 * - 首次进入且未选定项目时，自动选取列表第一项；
 * - 列表为空时直接给出"新建项目"入口，而不是假装有项目可用；
 * - 重命名面板读 `GET /projects/{id}` 的**权威单条**，而不是复用列表里那一行——
 *   列表可能是几十秒前的缓存，改名、切阶段之后列表未必已刷新。
 */
export function ProjectSwitcher() {
  const navigate = useNavigate();
  const projectId = useUiStore((state) => state.projectId);
  const setProjectId = useUiStore((state) => state.setProjectId);
  const pushToast = useUiStore((state) => state.pushToast);
  const projects = useProjects();
  const createProject = useCreateProject();

  /** 当前项目的权威详情（切换后即自动刷新）。 */
  const project = useProject(projectId);
  const updateProject = useUpdateProject(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");

  const items = projects.data ?? [];

  /**
   * 调和当前项目与真实列表（幽灵 ID 清理）。
   *
   * `projectId` 由 `zustand/persist` 落在 localStorage：库里换过一轮（重建 / 清库）之后，
   * 存下的 ID 会指向一个**已不存在的项目**，于是总览等所有项目级查询整齐地报
   * "项目不存在"。这里只做一件事——**等列表落定后**，若当前选择不在列表里就改选第一项；
   * 列表为空则清空选择，交由视图渲染空态，而不是拿着死 ID 一直发请求（CONSTITUTION D1）。
   *
   * 为什么必须等 `!isFetching`：新建项目的成功回调会先 `setProjectId(created.id)`
   * 再触发列表失效重取；此刻缓存里还是旧列表，若立即调和会把刚建的项目顶掉。
   */
  useEffect(() => {
    if (!projects.isSuccess || projects.isFetching) return;
    if (projectId && items.some((item) => item.id === projectId)) return;
    const first = items[0];
    if (first) {
      setProjectId(first.id);
    } else if (projectId) {
      setProjectId("");
    }
  }, [projectId, items, projects.isSuccess, projects.isFetching, setProjectId]);

  if (projects.isError) {
    return (
      <div className="topbar-status">
        <ErrorState title="项目列表加载失败" error={projects.error} />
      </div>
    );
  }

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createProject.mutate(
      { name: trimmed },
      {
        onSuccess: (created) => {
          setProjectId(created.id);
          setDialogOpen(false);
          setName("");
          navigate("/studio/classify");
        },
      },
    );
  };

  const submitRename = () => {
    const trimmed = renameName.trim();
    if (!trimmed) return;
    updateProject.mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          setRenameOpen(false);
          pushToast({ title: "项目已重命名", detail: trimmed, mark: "✓" });
        },
      },
    );
  };

  return (
    <>
      <div className="topbar-status" aria-label="当前项目">
        <span className="status-dot"></span>
        <Select
          id="project-switcher"
          aria-label="切换项目"
          value={projectId}
          disabled={projects.isPending}
          options={[
            ...(projectId ? [] : [{ value: "", label: "选择项目…" }]),
            ...items.map((project) => ({ value: project.id, label: project.name })),
            { value: NEW_PROJECT_VALUE, label: "＋ 新建项目…" },
          ]}
          onChange={(event) => {
            const value = event.target.value;
            if (value === NEW_PROJECT_VALUE) {
              setDialogOpen(true);
              return;
            }
            setProjectId(value);
          }}
        />
        {projectId ? (
          <Button
            size="small"
            variant="quiet"
            id="rename-project"
            disabled={project.isPending}
            onClick={() => {
              setRenameName(project.data?.name ?? "");
              setRenameOpen(true);
            }}
          >
            重命名
          </Button>
        ) : null}
      </div>

      <Modal
        eyebrow="立项"
        title="新建项目"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <Button variant="quiet" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={!name.trim() || createProject.isPending}
              onClick={submit}
            >
              {createProject.isPending ? "创建中…" : "创建并进入分类"}
            </Button>
          </>
        }
      >
        <Field id="new-project-name" label="项目名称" required>
          <TextInput
            id="new-project-name"
            value={name}
            maxLength={200}
            placeholder="例如：金怜盛放 · 第一季"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
          />
        </Field>
        {createProject.isError ? <ErrorState title="创建失败" error={createProject.error} /> : null}
      </Modal>

      <Modal
        eyebrow="项目设置"
        title="重命名项目"
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        footer={
          <>
            <Button variant="quiet" onClick={() => setRenameOpen(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              disabled={!renameName.trim() || updateProject.isPending}
              onClick={submitRename}
            >
              {updateProject.isPending ? "保存中…" : "保存"}
            </Button>
          </>
        }
      >
        <Field id="rename-project-name" label="项目名称" required>
          <TextInput
            id="rename-project-name"
            value={renameName}
            maxLength={200}
            onChange={(event) => setRenameName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submitRename();
            }}
          />
        </Field>

        {/* 权威详情来自 GET /projects/{id}，不是列表里那一行可能的旧缓存 */}
        {project.isError ? (
          <ErrorState title="项目详情加载失败" error={project.error} />
        ) : project.data ? (
          <div style={{ marginTop: 14 }}>
            <DefinitionList
              entries={[
                { term: "标识", value: project.data.slug },
                { term: "当前阶段", value: project.data.current_stage },
                { term: "方法包", value: project.data.methodology_version },
                { term: "锁定 Profile", value: project.data.locked_profile_version ?? "未锁定" },
              ]}
            />
          </div>
        ) : (
          <p className="inline-note" style={{ marginTop: 14 }}>
            加载中…
          </p>
        )}

        {updateProject.isError ? (
          <ErrorState title="重命名失败" error={updateProject.error} />
        ) : null}
      </Modal>
    </>
  );
}
